import { useMemo } from "react";
import { useContact } from "@/features/contacts/hooks";
import { useCompany } from "@/lib/hooks/api/companies";
import {
  useQuickBooksInvoicesByCustomerMultiCompany,
  useQuickBooksInvoicesByMultipleCustomersMultiCompany,
} from "@/lib/hooks/api/quickbooks/quickbooks-unified";
import { getCompanyQuickBooksCustomerRefs } from "@/lib/utils/crm/quickbooks-company-ref";
import { sortInvoicesNewestFirst } from "../utils/invoice-order";

export function useInvoiceData(customerName: string, contactId?: string) {
  const { data: contact } = useContact(contactId || "");
  const { data: company } = useCompany(contact?.company_id || "");

  const qbCustomerRefs = useMemo(
    () => getCompanyQuickBooksCustomerRefs(company),
    [company]
  );

  const qbCustomerIds = useMemo(
    () => qbCustomerRefs.map((ref) => ref.customerId),
    [qbCustomerRefs]
  );

  const effectiveName = useMemo(
    () => (qbCustomerIds.length > 0 ? undefined : customerName),
    [qbCustomerIds.length, customerName]
  );

  const singleCustomerHook =
    useQuickBooksInvoicesByCustomerMultiCompany(
      qbCustomerIds.length === 1 ? effectiveName : undefined,
      qbCustomerIds.length === 1
        ? qbCustomerRefs[0]?.customerId
        : undefined,
      qbCustomerIds.length === 1
        ? qbCustomerRefs[0]?.realmId
        : undefined
    );

  const multipleCustomersHook =
    useQuickBooksInvoicesByMultipleCustomersMultiCompany(
      qbCustomerIds.length > 1 ? qbCustomerRefs : []
    );

  const {
    data: invoiceData,
    isLoading: isLoadingInvoices,
    error: invoiceError,
  } =
    qbCustomerIds.length > 1
      ? multipleCustomersHook
      : singleCustomerHook;

  // #843: QuickBooks does not return invoices in date order, so the newest one
  // could appear anywhere in the list. Ordering lives in ../utils/invoice-order
  // so it can be tested without React or a QuickBooks connection.
  const sortedInvoices = useMemo(
    () => sortInvoicesNewestFirst(invoiceData?.items ?? []),
    [invoiceData?.items]
  );

  return {
    contact,
    company,
    qbCustomerRefs,
    qbCustomerIds,
    effectiveName,
    invoiceData,
    isLoadingInvoices,
    invoiceError,
    invoices: sortedInvoices,
  };
}
